/**
 * KAN-755: вкладка "Группы товаров": таблица групп, создание и правка
 * (ProductGroupFormDialog), удаление с подтверждением. Группу из скидки
 * удалить нельзя: 409 показывается текстом с названиями скидок.
 */
import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/telegram/ConfirmDialog';
import { useProductGroupMutations, useProductGroups } from '@/hooks/useDiscounts';
import { groupDeleteErrorMessage } from '@/lib/discountForm';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import type { ProductGroup } from '@/types/discount';
import { ProductGroupFormDialog } from './ProductGroupFormDialog';
import { ProductGroupsTable } from './ProductGroupsTable';

export function ProductGroupsTab({ canEdit }: { canEdit: boolean }) {
  const groups = useProductGroups();
  const { remove } = useProductGroupMutations();
  const [dialog, setDialog] = useState<{ open: boolean; id: string | null }>({ open: false, id: null });
  const [toDelete, setToDelete] = useState<ProductGroup | null>(null);
  const items = groups.data ?? [];

  let content: JSX.Element;
  if (groups.isLoading) content = <p className="text-sm text-gray-500">Загрузка...</p>;
  else if (groups.isError) content = <p className="text-sm text-red-700">{extractErrorMessage(groups.error, 'Не удалось загрузить группы')}</p>;
  else if (items.length === 0) {
    content = (
      <p className="rounded-lg border bg-white p-6 text-center text-sm text-gray-500" data-testid="groups-empty">
        Групп пока нет
      </p>
    );
  } else {
    content = (
      <ProductGroupsTable
        items={items}
        canEdit={canEdit}
        onEdit={(group) => setDialog({ open: true, id: group.id })}
        onDelete={(group) => {
          remove.reset();
          setToDelete(group);
        }}
      />
    );
  }

  return (
    <div className="space-y-3" data-testid="groups-tab">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-gray-600">Группа объединяет товары для скидки, например "Новинки" или "Распродажа".</p>
        {canEdit && (
          <Button className="ml-auto" onClick={() => setDialog({ open: true, id: null })} data-testid="group-create">
            <Plus className="mr-1 h-4 w-4" />
            Новая группа
          </Button>
        )}
      </div>
      {remove.error && (
        <p role="alert" className="text-sm text-red-700" data-testid="group-delete-error">
          {groupDeleteErrorMessage(remove.error)}
        </p>
      )}
      {content}
      {canEdit && (
        <>
          <ProductGroupFormDialog
            open={dialog.open}
            groupId={dialog.id}
            onOpenChange={(open) => setDialog((prev) => ({ ...prev, open }))}
          />
          <ConfirmDialog
            open={toDelete !== null}
            onOpenChange={(open) => {
              if (!open) setToDelete(null);
            }}
            testId="group-delete-confirm"
            view={{
              title: `Удалить группу "${toDelete?.name ?? ''}"?`,
              description: 'Товары останутся в каталоге, удалится только группа.',
              confirm: 'Удалить',
            }}
            onConfirm={() => {
              if (toDelete) remove.mutate(toDelete.id);
              setToDelete(null);
            }}
          />
        </>
      )}
    </div>
  );
}
